import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { Alert, Box, Chip, MenuItem, Select, Snackbar, Stack, Typography } from '@mui/material'
import {
  Button as FxButton,
  Icon as FxIcon,
  Pill,
  SegmentedControl,
} from '@/components/ui'
import {
  DataGrid,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridRowSelectionModel,
  GridActionsCellItem,
  GridRowModes,
  GridRowModesModel,
  GridEventListener,
  GridRowEditStopReasons,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
} from '@mui/x-data-grid'
import { deDE } from '@mui/x-data-grid/locales'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  FactCheck as FactCheckIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'
import { usePOTracking } from '@/hooks/usePOTracking'
import { useAuth } from '@/hooks/useAuth'
import { useUserPreference } from '@/hooks/useUserPreference'
import { useT } from '@/i18n/useT'
import { EvidenceDialog } from '@/components/InvoiceAnalyser/EvidenceDialog'
import { DocumentPreviewDrawer } from './DocumentPreviewDrawer'
import type { DatabaseInvoice, ColumnDefinition } from '@/types/database'
import type { InvoiceExtraction, Evidence } from '@/types/invoiceAnalyser'
import type { PurchaseOrder } from '@/types/po'
import {
  STATUS_OPTIONS,
  STATUS_LABELS,
  STATUS_TOKENS,
  normalizeStatus,
  isOverdue,
  isDueSoon,
  todayIso,
  type InvoiceStatus,
} from '@/utils/invoiceStatus'
import { ENTITY_OPTIONS } from '@/utils/invoiceEntity'
import { LEGAL_COUNSEL_OPTIONS } from '@/utils/legalCounsel'
import {
  DOCUMENT_TYPE_OPTIONS,
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_TYPE_COLORS,
  normalizeDocumentType,
  signMismatchWarning,
} from '@/utils/documentType'

/** Column view presets to keep the table focused on invoice control by default. */
type ViewPreset = 'standard' | 'tax' | 'all'

const STANDARD_VISIBLE = new Set<string>([
  'actions', 'status', 'documentType', 'supplierName', 'recipient', 'entity', 'projectName',
  'rechnungsnummer', 'originalInvoiceId', 'dateInvoice', 'dueDate', 'servicePeriodStart', 'servicePeriodEnd',
  'netto', 'brutto', 'auslagen', 'currency', 'poId', 'approver', 'legalCounsel', 'comment',
])

const TAX_VISIBLE = new Set<string>([
  ...STANDARD_VISIBLE,
  'vatAmount', 'taxFreeAmount', 'taxableNetAmount', 'ustId', 'original',
])

const ALL_COLUMN_FIELDS = [
  'actions', 'status', 'documentType', 'supplierName', 'recipient', 'entity', 'projectName',
  'rechnungsnummer', 'originalInvoiceId', 'dateInvoice', 'dueDate', 'servicePeriodStart', 'servicePeriodEnd',
  'netto', 'brutto', 'auslagen', 'currency',
  'poId', 'approver', 'approvalDate', 'legalCounsel', 'vatAmount', 'taxFreeAmount',
  'taxableNetAmount', 'ustId', 'confidence', 'comment', 'source',
  'sourceDocumentId', 'original', 'createdAt',
]

/** What is remembered per user of the grid layout (column order is a Pro feature). */
interface GridLayout {
  preset: ViewPreset
  visibility: GridColumnVisibilityModel
  widths: Record<string, number>
}

function buildVisibilityModel(preset: ViewPreset): GridColumnVisibilityModel {
  if (preset === 'all') return {}
  const visible = preset === 'tax' ? TAX_VISIBLE : STANDARD_VISIBLE
  const model: GridColumnVisibilityModel = {}
  ALL_COLUMN_FIELDS.forEach((field) => {
    if (!visible.has(field)) model[field] = false
  })
  return model
}

const EMPTY_EVIDENCE: Evidence = {
  supplier: null, invoice_date: null, invoice_number: null, currency: null,
  total_net: null, total_gross: null, vat_amount: null, tax_free_amount: null,
  taxable_net_amount: null, project_name: null, recipient: null,
}

/** True when a saved invoice carries analyser audit data worth showing. */
const hasAuditTrail = (inv: DatabaseInvoice): boolean =>
  !!inv.evidence || (inv.warnings?.length ?? 0) > 0

/** Adapt a stored DatabaseInvoice back into the InvoiceExtraction shape EvidenceDialog expects. */
function toExtraction(inv: DatabaseInvoice): InvoiceExtraction {
  return {
    supplier: inv.supplierName,
    invoice_date: inv.dateInvoice,
    invoice_number: inv.rechnungsnummer,
    currency: inv.currency,
    total_net: inv.netto,
    total_gross: inv.brutto,
    vat_amount: inv.vatAmount,
    tax_free_amount: inv.taxFreeAmount,
    taxable_net_amount: inv.taxableNetAmount,
    vat_breakdown: inv.vatBreakdown,
    tax_free_breakdown: inv.taxFreeBreakdown,
    project_name: inv.projectName,
    recipient: inv.recipient,
    ust_id: inv.ustId,
    po_id: inv.poId,
    evidence: inv.evidence ?? EMPTY_EVIDENCE,
    warnings: inv.warnings ?? [],
    confidence: inv.confidence ?? 'low',
    document_type: inv.documentType ?? 'invoice',
  }
}

const MAX_PO_DESCRIPTION_LENGTH = 10

const truncateDescription = (description: string | null): string => {
  if (!description) return ''
  const trimmed = description.trim()
  if (!trimmed) return ''
  return trimmed.length > MAX_PO_DESCRIPTION_LENGTH
    ? `${trimmed.slice(0, MAX_PO_DESCRIPTION_LENGTH)}...`
    : trimmed
}

const formatPOLabel = (po: PurchaseOrder): string => {
  const truncated = truncateDescription(po.description)
  return truncated ? `${po.poNumber} - ${truncated}` : po.poNumber
}

/** Short reference label for linking a Storno/Gutschrift to its original invoice. */
const formatInvoiceRef = (inv: DatabaseInvoice): string => {
  const num = inv.rechnungsnummer || '—'
  const date = inv.dateInvoice ? ` · ${inv.dateInvoice}` : ''
  return `${num}${date}`
}

const getInvoiceYear = (dateStr: string | null): number | null => {
  if (!dateStr) return null
  const trimmed = dateStr.trim()
  if (!trimmed) return null

  const isoMatch = /^(\d{4})-\d{2}-\d{2}/.exec(trimmed)
  if (isoMatch) return Number(isoMatch[1])

  const deMatch = /^(\d{2})\.(\d{2})\.(\d{4})/.exec(trimmed)
  if (deMatch) return Number(deMatch[3])

  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.getFullYear()
}

/**
 * Display any stored invoice date consistently as DD.MM.YYYY, regardless of how it was
 * stored (analyser saves ISO YYYY-MM-DD; dashboard/Excel import use DD.MM.YYYY).
 */
const formatDateCell = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const s = String(value).trim()
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`
  return s
}

interface DatabaseTableProps {
  invoices: DatabaseInvoice[]
  columns: ColumnDefinition[]
  highlightId?: string | null
  onEdit: (id: string, data: Partial<DatabaseInvoice>) => Promise<void> | void
  onDelete: (id: string) => Promise<void> | void
  onCreate: (data: Partial<DatabaseInvoice>) => Promise<void> | void
  onBulkStatus: (
    ids: string[],
    status: InvoiceStatus,
    approver?: string | null,
    approvalDate?: string | null,
  ) => Promise<void> | void
}

export function DatabaseTable({
  invoices,
  highlightId,
  onEdit,
  onDelete,
  onCreate,
  onBulkStatus,
}: DatabaseTableProps) {
  const apiBaseUrl = (import.meta.env.VITE_API_URL || '/v1').replace(/\/$/, '')
  const { user } = useAuth()
  const { t, lang } = useT()
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({})
  const [rows, setRows] = useState<DatabaseInvoice[]>(invoices)
  const [viewPreset, setViewPreset] = useState<ViewPreset>('standard')
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    () => buildVisibilityModel('standard')
  )
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  const gridLayout = useUserPreference<GridLayout>('database.grid')
  const layoutRestored = useRef(false)
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([])
  const [bulkStatus, setBulkStatus] = useState<InvoiceStatus>('approved')
  const [auditInvoice, setAuditInvoice] = useState<DatabaseInvoice | null>(null)
  const [previewInvoice, setPreviewInvoice] = useState<DatabaseInvoice | null>(null)
  const [signWarning, setSignWarning] = useState<string | null>(null)

  // PO Tracking - load all POs for per-row year filtering
  const { usePOs } = usePOTracking()
  const { data: pos = [] } = usePOs()

  // The grid's singleSelect options carry their own labels, so they have to be
  // translated here rather than in the shared constants (plain TS, no hook).
  const statusOptions = useMemo(
    () => STATUS_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) })),
    [t],
  )
  const documentTypeOptions = useMemo(
    () => DOCUMENT_TYPE_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) })),
    [t],
  )
  const entityOptions = useMemo(
    () => ENTITY_OPTIONS.map((o) => ({ value: o.value, label: o.value ? o.label : t('Unassigned') })),
    [t],
  )
  const legalCounselOptions = useMemo(
    () =>
      LEGAL_COUNSEL_OPTIONS.map((o) => ({
        value: o.value,
        label: o.value ? o.label : t('Unassigned'),
      })),
    [t],
  )

  // Restore the user's saved layout once the server has answered
  useEffect(() => {
    if (!gridLayout.loaded || layoutRestored.current) return
    layoutRestored.current = true
    const saved = gridLayout.value
    if (!saved) return
    setViewPreset(saved.preset ?? 'standard')
    setColumnVisibilityModel(saved.visibility ?? buildVisibilityModel(saved.preset ?? 'standard'))
    setColumnWidths(saved.widths ?? {})
  }, [gridLayout.loaded, gridLayout.value])

  // Latest layout in a ref so the save callbacks stay stable — the toolbar is
  // memoised on them and would otherwise remount (and drop search focus).
  const currentLayout = useRef<GridLayout>({ preset: viewPreset, visibility: columnVisibilityModel, widths: columnWidths })
  currentLayout.current = { preset: viewPreset, visibility: columnVisibilityModel, widths: columnWidths }
  const { save: saveGridLayout } = gridLayout
  const saveLayout = useCallback(
    (patch: Partial<GridLayout>) => {
      saveGridLayout({ ...currentLayout.current, ...patch })
    },
    [saveGridLayout],
  )

  const handleViewPresetChange = useCallback(
    (next: ViewPreset) => {
      const visibility = buildVisibilityModel(next)
      setViewPreset(next)
      setColumnVisibilityModel(visibility)
      saveLayout({ preset: next, visibility })
    },
    [saveLayout],
  )

  const handleColumnVisibilityChange = useCallback(
    (visibility: GridColumnVisibilityModel) => {
      setColumnVisibilityModel(visibility)
      saveLayout({ visibility })
    },
    [saveLayout],
  )

  const handleColumnWidthChange = useCallback<GridEventListener<'columnWidthChange'>>(
    (params) => {
      const widths = { ...currentLayout.current.widths, [params.colDef.field]: Math.round(params.width) }
      setColumnWidths(widths)
      saveLayout({ widths })
    },
    [saveLayout],
  )

  // Bulk status change for all selected rows (auto-stamps approver when approving)
  const handleApplyBulk = useCallback(async () => {
    const ids = selectionModel.map(String)
    if (ids.length === 0) return
    let approver: string | null | undefined
    let approvalDate: string | null | undefined
    if (bulkStatus === 'approved') {
      approver = user?.fullName || user?.username || null
      approvalDate = todayIso()
    }
    await onBulkStatus(ids, bulkStatus, approver, approvalDate)
    setSelectionModel([])
  }, [selectionModel, bulkStatus, onBulkStatus, user])

  // Update rows when invoices change
  useEffect(() => {
    setRows(invoices)
  }, [invoices])

  // Handle row edit stop
  const handleRowEditStop: GridEventListener<'rowEditStop'> = useCallback((params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true
    }
  }, [])

  // Handle edit click
  const handleEditClick = useCallback((id: string) => () => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.Edit } }))
  }, [])

  // Handle save click
  const handleSaveClick = useCallback((id: string) => () => {
    setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View } }))
  }, [])

  // Handle delete click
  const handleDeleteClick = useCallback((id: string) => () => {
    void onDelete(id)
  }, [onDelete])

  // Handle cancel click
  const handleCancelClick = useCallback((id: string) => () => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    }))
  }, [])

  // Process row update
  const processRowUpdate = useCallback(async (newRow: DatabaseInvoice, oldRow: DatabaseInvoice) => {
    const updatedRow = { ...newRow }

    // Auto-stamp approver + approval date when an invoice is marked approved.
    if (normalizeStatus(updatedRow.status) === 'approved' && !updatedRow.approver) {
      updatedRow.approver = user?.fullName || user?.username || null
      if (!updatedRow.approvalDate) updatedRow.approvalDate = todayIso()
    }

    // Non-blocking plausibility nudge: credit notes / cancellations are usually
    // negative. Sign is never changed automatically — we only warn.
    const mismatch = signMismatchWarning(updatedRow.documentType, updatedRow.brutto)
    if (mismatch) setSignWarning(mismatch)

    const isNewRow = oldRow.id.startsWith('new-')
    setRows((prev) => prev.map((row) => (row.id === oldRow.id ? updatedRow : row)))

    try {
      if (isNewRow) {
        await onCreate(updatedRow)
      } else {
        await onEdit(updatedRow.id, updatedRow)
      }
      return updatedRow
    } catch (error) {
      setRows((prev) => {
        if (isNewRow) {
          return prev.filter((row) => row.id !== updatedRow.id)
        }
        return prev.map((row) => (row.id === updatedRow.id ? oldRow : row))
      })
      throw error instanceof Error ? error : new Error(t('Could not save the row'))
    }
  }, [onCreate, onEdit, user])

  // Handle row modes model change
  const handleRowModesModelChange = useCallback((newModel: GridRowModesModel) => {
    setRowModesModel(newModel)
  }, [])

  // Handle add new row
  const handleAddNew = useCallback(() => {
    const newId = `new-${Date.now()}`
    const newRow: DatabaseInvoice = {
      id: newId,
      supplierName: '',
      dateInvoice: null,
      brutto: null,
      netto: null,
      vatAmount: null,
      taxFreeAmount: null,
      taxableNetAmount: null,
      rechnungsnummer: '',
      comment: '',
      currency: null,
      projectName: null,
      recipient: null,
      entity: null,
      ustId: null,
      confidence: null,
      source: 'dashboard',
      customFields: {},
      vatBreakdown: null,
      taxFreeBreakdown: null,
      sourceDocumentId: null,
      poId: null,
      originalCurrency: null,
      originalBrutto: null,
      originalNetto: null,
      originalVatAmount: null,
      originalTaxFreeAmount: null,
      originalTaxableNetAmount: null,
      fxRate: null,
      fxRateDate: null,
      fxRateSource: null,
      status: 'open',
      approver: null,
      approvalDate: null,
      dueDate: null,
      auslagen: null,
      originalAuslagen: null,
      servicePeriodStart: null,
      servicePeriodEnd: null,
      legalCounsel: null,
      documentType: 'invoice',
      originalInvoiceId: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    setRows((prev) => [newRow, ...prev])
    setRowModesModel((prev) => ({ ...prev, [newId]: { mode: GridRowModes.Edit, fieldToFocus: 'supplierName' } }))
  }, [])

  // Base columns
  const baseColumns: GridColDef[] = [
    {
      field: 'actions',
      type: 'actions',
      headerName: t('Actions'),
      width: 160,
      cellClassName: 'actions',
      getActions: ({ id, row }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label={t('Save')}
              sx={{ color: 'primary.main' }}
              onClick={handleSaveClick(id.toString())}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label={t('Cancel')}
              className="textPrimary"
              onClick={handleCancelClick(id.toString())}
              color="inherit"
            />,
          ]
        }

        return [
          ...((row as DatabaseInvoice).sourceDocumentId
            ? [
                <GridActionsCellItem
                  icon={<VisibilityIcon />}
                  label={t('View invoice')}
                  onClick={() => setPreviewInvoice(row as DatabaseInvoice)}
                  color="inherit"
                />,
              ]
            : []),
          <GridActionsCellItem
            icon={<EditIcon />}
            label={t('Edit')}
            className="textPrimary"
            onClick={handleEditClick(id.toString())}
            color="inherit"
          />,
          ...(hasAuditTrail(row as DatabaseInvoice)
            ? [
                <GridActionsCellItem
                  icon={<FactCheckIcon />}
                  label={t('Audit trail')}
                  onClick={() => setAuditInvoice(row as DatabaseInvoice)}
                  color="inherit"
                />,
              ]
            : []),
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label={t('Delete')}
            onClick={handleDeleteClick(id.toString())}
            color="inherit"
          />,
        ]
      },
    },
    {
      field: 'status',
      headerName: t('Status'),
      width: 150,
      editable: true,
      type: 'singleSelect',
      valueOptions: statusOptions,
      renderCell: (params) => {
        const status = normalizeStatus(params.value)
        const row = params.row as DatabaseInvoice
        const overdue = isOverdue(row.dueDate, status, row.documentType)
        return (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
            <Pill
              fg={STATUS_TOKENS[status].fg}
              bg={STATUS_TOKENS[status].bg}
              border={STATUS_TOKENS[status].bd}
            >
              {t(STATUS_LABELS[status])}
            </Pill>
            {overdue && (
              <Pill fg="var(--err)" bg="var(--errSoft)" border="var(--err)">
                {t('Overdue')}
              </Pill>
            )}
          </Stack>
        )
      },
    },
    {
      field: 'documentType',
      headerName: t('Type'),
      width: 140,
      editable: true,
      type: 'singleSelect',
      valueOptions: documentTypeOptions,
      renderCell: (params) => {
        const dt = normalizeDocumentType(params.value)
        const row = params.row as DatabaseInvoice
        const warn = signMismatchWarning(dt, row.brutto)
        return (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
            <Chip size="small" label={t(DOCUMENT_TYPE_LABELS[dt])} color={DOCUMENT_TYPE_COLORS[dt]} />
            {warn && (
              <Box component="span" title={t(warn)} sx={{ color: 'warning.main', cursor: 'help', fontWeight: 700 }}>
                ⚠
              </Box>
            )}
          </Stack>
        )
      },
    },
    { field: 'supplierName', headerName: t('Supplier'), width: 180, editable: true },
    {
      field: 'dateInvoice',
      headerName: t('Date'),
      width: 120,
      editable: true,
      type: 'string',
      valueFormatter: (value) => formatDateCell(value),
    },
    {
      field: 'dueDate',
      headerName: t('Due'),
      width: 120,
      editable: true,
      type: 'string',
      renderCell: (params) => {
        if (!params.value) return '–'
        const row = params.row as DatabaseInvoice
        const overdue = isOverdue(row.dueDate, row.status, row.documentType)
        const soon = !overdue && isDueSoon(row.dueDate, row.status, row.documentType)
        const color = overdue ? 'error.main' : soon ? 'warning.main' : 'inherit'
        return (
          <Box
            component="span"
            sx={{ color, fontWeight: overdue || soon ? 600 : 400 }}
          >
            {formatDateCell(params.value)}
          </Box>
        )
      },
    },
    {
      field: 'servicePeriodStart',
      headerName: t('Service from'),
      width: 120,
      editable: true,
      type: 'string',
      valueFormatter: (value) => formatDateCell(value),
    },
    {
      field: 'servicePeriodEnd',
      headerName: t('Service to'),
      width: 120,
      editable: true,
      type: 'string',
      valueFormatter: (value) => formatDateCell(value),
    },
    {
      field: 'brutto',
      headerName: t('Gross'),
      width: 100,
      editable: true,
      type: 'number',
      valueFormatter: (value) => value !== null && value !== undefined ? Number(value).toFixed(2) : '',
    },
    {
      field: 'netto',
      headerName: t('Net'),
      width: 100,
      editable: true,
      type: 'number',
      valueFormatter: (value) => value !== null && value !== undefined ? Number(value).toFixed(2) : '',
    },
    {
      field: 'vatAmount',
      headerName: t('VAT'),
      width: 100,
      editable: true,
      type: 'number',
      valueFormatter: (value) => value !== null && value !== undefined ? Number(value).toFixed(2) : '',
    },
    {
      field: 'taxFreeAmount',
      headerName: t('Tax-free'),
      width: 110,
      editable: true,
      type: 'number',
      valueFormatter: (value) => value !== null && value !== undefined ? Number(value).toFixed(2) : '',
    },
    {
      field: 'taxableNetAmount',
      headerName: t('Taxable net'),
      width: 120,
      editable: true,
      type: 'number',
      valueFormatter: (value) => value !== null && value !== undefined ? Number(value).toFixed(2) : '',
    },
    {
      field: 'auslagen',
      headerName: t('Expenses'),
      width: 110,
      editable: true,
      type: 'number',
      valueFormatter: (value) => value !== null && value !== undefined ? Number(value).toFixed(2) : '',
    },
    { field: 'rechnungsnummer', headerName: t('Invoice #'), width: 140, editable: true },
    {
      field: 'originalInvoiceId',
      headerName: t('Original invoice'),
      width: 220,
      editable: true,
      type: 'singleSelect',
      valueOptions: (params: any) => {
        const row = params.row as DatabaseInvoice
        const options: { value: string | null; label: string }[] = [{ value: null, label: '—' }]
        const supplierKey = (row.supplierName || '').trim().toLowerCase()
        rows.forEach((inv) => {
          if (inv.id === row.id || inv.id.startsWith('new-')) return
          if (inv.documentType && inv.documentType !== 'invoice') return // link to an actual invoice
          if (supplierKey && (inv.supplierName || '').trim().toLowerCase() !== supplierKey) return
          options.push({ value: inv.id, label: formatInvoiceRef(inv) })
        })
        // Keep the currently linked original selectable even if the supplier filter would hide it.
        if (row.originalInvoiceId && !options.some((o) => o.value === row.originalInvoiceId)) {
          const linked = rows.find((i) => i.id === row.originalInvoiceId)
          if (linked) options.push({ value: linked.id, label: formatInvoiceRef(linked) })
        }
        return options
      },
      valueFormatter: (value: string | null) => {
        if (!value) return '—'
        const inv = rows.find((i) => i.id === value)
        return inv ? formatInvoiceRef(inv) : '—'
      },
    },
    { field: 'comment', headerName: t('Comment'), width: 200, editable: true },
    { field: 'currency', headerName: t('Currency'), width: 80, editable: true },
    { field: 'projectName', headerName: t('Project'), width: 150, editable: true },
    { field: 'recipient', headerName: t('Recipient'), width: 180, editable: true },
    {
      field: 'entity',
      headerName: t('Entity'),
      width: 200,
      editable: true,
      type: 'singleSelect',
      valueOptions: entityOptions,
      valueFormatter: (value: string | null) => value || t('Unassigned'),
    },
    { field: 'ustId', headerName: t('VAT ID'), width: 120, editable: true },
    { field: 'confidence', headerName: t('Confidence'), width: 100, editable: true },
    { field: 'source', headerName: t('Origin'), width: 100, editable: false },
    {
      field: 'sourceDocumentId',
      headerName: t('Source'),
      width: 140,
      editable: false,
      renderCell: (params) => {
        if (!params.value) return '-'
        const href = `${apiBaseUrl}/documents/${params.value}/file`
        return (
          <a href={href} target="_blank" rel="noreferrer">
            {t('Download')}
          </a>
        )
      },
    },
    {
      field: 'poId',
      headerName: t('PO number'),
      width: 150,
      editable: true,
      type: 'singleSelect',
      valueOptions: useMemo(() => {
        return (params) => {
          const row = params.row as DatabaseInvoice
          const options = [{ value: null as string | null, label: t('Unassigned') }]
          if (pos.length === 0) return options

          const invoiceYear = getInvoiceYear(row.dateInvoice)
          const filtered = invoiceYear ? pos.filter((po) => po.year === invoiceYear) : pos

          filtered.forEach((po: PurchaseOrder) => {
            options.push({ value: po.id, label: formatPOLabel(po) })
          })

          if (row.poId) {
            const selected = pos.find((po) => po.id === row.poId)
            const alreadyIncluded = selected && filtered.some((po) => po.id === selected.id)
            if (selected && !alreadyIncluded) {
              options.push({ value: selected.id, label: formatPOLabel(selected) })
            }
          }

          return options
        }
      }, [pos, t]),
      valueFormatter: (value: string | null) => {
        const po = pos.find((p: PurchaseOrder) => p.id === value)
        return po ? formatPOLabel(po) : t('Unassigned')
      },
    },
    { field: 'approver', headerName: t('Approver'), width: 160, editable: true },
    {
      field: 'legalCounsel',
      headerName: t('Legal counsel'),
      width: 180,
      editable: true,
      type: 'singleSelect',
      valueOptions: legalCounselOptions,
      valueFormatter: (value: string | null) => value || t('Unassigned'),
    },
    {
      field: 'approvalDate',
      headerName: t('Approved on'),
      width: 130,
      editable: true,
      type: 'string',
      valueFormatter: (value) => formatDateCell(value),
    },
    {
      field: 'original',
      headerName: t('Original'),
      width: 180,
      editable: false,
      renderCell: (params) => {
        const row = params.row as DatabaseInvoice
        if (!row.originalCurrency || row.originalNetto == null) return '-'
        const value = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2 }).format(row.originalNetto)
        return `${value} ${row.originalCurrency} (FX ${row.fxRateDate || '-'})`
      },
    },
    {
      field: 'createdAt',
      headerName: t('Created'),
      width: 160,
      editable: false,
      valueFormatter: (value) => {
        if (!value) return ''
        return new Date(value).toLocaleString('de-DE')
      },
    },
  ]

  const Toolbar = useCallback(() => (
    <GridToolbarContainer
      sx={{
        gap: 1,
        p: '12px 14px',
        borderBottom: '1px solid var(--line)',
        flexWrap: 'wrap',
      }}
    >
      <GridToolbarQuickFilter
        placeholder={t('Search invoice number, supplier, PO …')}
        sx={{ minWidth: 240, flex: 1 }}
      />
      <GridToolbarFilterButton />
      <GridToolbarColumnsButton />
      <Box sx={{ flexGrow: 1 }} />
      {selectionModel.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 1 }}>
          <Typography variant="body2" sx={{ color: 'var(--muted)' }}>
            {selectionModel.length} {t('selected')}
          </Typography>
          <Select
            size="small"
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value as InvoiceStatus)}
            sx={{ minWidth: 130 }}
          >
            {statusOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>{`${t('Set')}: ${o.label}`}</MenuItem>
            ))}
          </Select>
          <FxButton variant="accent" size="sm" onClick={() => void handleApplyBulk()}>
            {t('Apply')}
          </FxButton>
        </Stack>
      )}
      <SegmentedControl<ViewPreset>
        value={viewPreset}
        onChange={handleViewPresetChange}
        options={[
          { value: 'standard', label: t('Standard') },
          { value: 'tax', label: t('Tax details') },
          { value: 'all', label: t('All') },
        ]}
      />
      <FxButton variant="outline" size="sm" onClick={handleAddNew}>
        <FxIcon name="plus" size={14} strokeWidth={2.4} />
        {t('Add invoice')}
      </FxButton>
    </GridToolbarContainer>
  ), [handleAddNew, viewPreset, handleViewPresetChange, selectionModel, bulkStatus, handleApplyBulk, statusOptions, t])

  return (
    <Box
      sx={{
        height: 640,
        width: '100%',
        border: '1px solid var(--line)',
        borderRadius: '12px',
        overflow: 'hidden',
        background: 'var(--paper)',
      }}
    >
      <EvidenceDialog
        open={!!auditInvoice}
        onClose={() => setAuditInvoice(null)}
        extraction={auditInvoice ? toExtraction(auditInvoice) : null}
      />
      <DocumentPreviewDrawer invoice={previewInvoice} onClose={() => setPreviewInvoice(null)} />
      <Snackbar
        open={!!signWarning}
        autoHideDuration={6000}
        onClose={() => setSignWarning(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setSignWarning(null)}>
          {signWarning ? t(signWarning) : null}
        </Alert>
      </Snackbar>
      <DataGrid
        rows={rows}
        columns={baseColumns.map((col) =>
          columnWidths[col.field] ? { ...col, width: columnWidths[col.field] } : col,
        )}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityChange}
        onColumnWidthChange={handleColumnWidthChange}
        checkboxSelection
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={setSelectionModel}
        keepNonExistentRowsSelected
        processRowUpdate={processRowUpdate}
        getRowClassName={(params) => (highlightId && params.id === highlightId ? 'invoice-highlight' : '')}
        pageSizeOptions={[25, 50, 100]}
        pagination
        initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
        disableRowSelectionOnClick
        /* The grid's own chrome — pagination, filter panel, column menu — is
           MUI's, not ours, so it needs MUI's German pack to switch with the
           rest of the page. English is the grid's built-in default. */
        localeText={lang === 'de' ? deDE.components.MuiDataGrid.defaultProps.localeText : undefined}
        slots={{
          toolbar: Toolbar,
        }}
        /* Styled onto the design tokens: dark uppercase header band, zebra
           rows, hairline borders — the grid keeps its inline editing and bulk
           selection while reading like the mockup's tables. */
        sx={{
          border: 'none',
          color: 'var(--text)',
          fontSize: 13,
          '--DataGrid-containerBackground': 'var(--tile)',
          '--DataGrid-rowBorderColor': 'var(--line2)',
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: 'var(--tile)',
            color: 'var(--onTileMuted)',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          },
          '& .MuiDataGrid-columnSeparator': { color: 'var(--line2)' },
          '& .MuiDataGrid-cell': { borderColor: 'var(--line2)' },
          '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: 'var(--zebra)' },
          '& .MuiDataGrid-row:hover': { backgroundColor: 'var(--hover)' },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid var(--line)',
            backgroundColor: 'var(--paper)',
          },
          '& .MuiTablePagination-root': { color: 'var(--muted)' },
          '& .MuiDataGrid-overlay': { backgroundColor: 'var(--paper)' },
          '& .invoice-highlight': {
            backgroundColor: 'var(--warnSoft)',
            boxShadow: 'inset 3px 0 0 var(--warn)',
            '&:hover': { backgroundColor: 'var(--warnSoft)' },
          },
        }}
      />
    </Box>
  )
}
